import * as React from 'react'
import { Link } from 'react-router-dom'
import type { BookingDto } from '@lib/dto/sales-booking.dto'
import type { BookingPaymentDto } from '@lib/dto/sales-payment.dto'
import { PAYMENT_STATUS_LABELS } from '@lib/enums/sales-payment.enum'
import { dollarsFromCents } from '@lib/helpers/format'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { useToast } from '@/components/ui/toast'
import { Card } from '@/components/ui/card'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useTurnOffAutopay } from '@/hooks/useSales'
import { CopyPayLink, dueLabel, PAYMENT_TONES } from './PaymentsPanel'

/**
 * What the couple owes under what they signed, and paying it
 * (docs/sales.spec.md, "Paying"): each payment with its own link, which they
 * can open or send to whoever is paying, a parent say. Their planner sees the
 * same without the links.
 */
/** Autopay is on: with what, and switching it off. */
function Autopay({ eventId, label }: { eventId: string; label: string }) {
  const { toast } = useToast()
  const turnOff = useTurnOffAutopay(eventId)
  const [confirming, setConfirming] = React.useState(false)
  return (
    <Card className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
      <span className="text-ink">
        Autopay is on with your {label}. <span className="text-ink-muted">You’re emailed three days before each charge.</span>
      </span>
      <Button variant="outline" size="sm" onClick={() => setConfirming(true)}>
        Turn off
      </Button>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Turn autopay off?"
        description="Later payments won’t be charged automatically. Pay each one from its link when it’s due."
        confirmLabel="Turn off"
        busy={turnOff.isPending}
        error={turnOff.error === null ? null : requestErrorMessage(turnOff.error)}
        onConfirm={() =>
          turnOff.mutate(undefined, {
            onSuccess: () => {
              setConfirming(false)
              toast('Autopay is off.')
            },
          })
        }
      />
    </Card>
  )
}

/** What came back to them on a payment: a refund, or a security deposit returned or kept, with why. */
function backLine(payment: BookingPaymentDto): string | null {
  const settled = payment.depositReturn
  if (settled !== null) return settled.returnedCents === 0 ? `Kept: ${settled.reason}` : `${dollarsFromCents(settled.returnedCents)} returned: ${settled.reason}`
  return payment.refundedCents === 0 ? null : `${dollarsFromCents(payment.refundedCents)} refunded`
}

export function BookingPayments({ booking }: { booking: BookingDto }) {
  // A payment a change to the agreement replaced, with nothing paid on it, is nothing to the couple.
  const payments = booking.payments.filter((p) => p.status !== 'canceled' || p.creditedCents > 0)
  if (payments.length === 0) return null
  return (
    <section className="space-y-3 border-t border-line pt-6">
      <h2 className="m-0 text-lg font-semibold text-ink">Your payments</h2>
      {booking.autopay === null ? null : <Autopay eventId={booking.eventId} label={booking.autopay.label} />}
      <ul className="m-0 list-none divide-y divide-line rounded-lg border border-line p-0">
        {payments.map((p) => {
          const open = p.status !== 'paid' && p.status !== 'stopped' && p.status !== 'canceled'
          const back = backLine(p)
          return (
            <li key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
              <div className="min-w-40 flex-1">
                <p className="m-0 text-sm font-medium text-ink">{p.label}</p>
                <p className="m-0 text-xs text-ink-muted">{dueLabel(p.dueOn)}</p>
                {back === null ? null : <p className="m-0 text-xs text-ink-muted">{back}</p>}
              </div>
              <span className="text-sm tabular-nums text-ink">{dollarsFromCents(p.amountCents)}</span>
              <Badge tone={PAYMENT_TONES[p.status]}>{PAYMENT_STATUS_LABELS[p.status]}</Badge>
              {open && p.payPath !== null ? (
                <span className="flex gap-1">
                  <Button asChild size="sm">
                    <Link to={p.payPath}>Pay</Link>
                  </Button>
                  <CopyPayLink payPath={p.payPath} />
                </span>
              ) : null}
            </li>
          )
        })}
      </ul>
      <p className="m-0 text-xs text-ink-muted">Anyone with a payment’s link can pay it, so you can send it to whoever is paying.</p>
    </section>
  )
}
